import type { ReactNode, SVGProps } from "react";
import type { LoadingType } from "@/lib/calculators/truck-loading-merged/types";

function PalletRect({
  x,
  y,
  w,
  h,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
}) {
  return <rect x={x} y={y} width={w} height={h} fill="#f97316" rx={0.5} />;
}

function truckCabParts(
  cabX: number,
  cabY: number,
  cabW: number,
  cabH: number,
): Array<SVGProps<SVGRectElement>> {
  return [
    { x: cabX, y: cabY, width: cabW, height: cabH, rx: 1.5, fill: "#e8943a", stroke: "#c47520", strokeWidth: 0.4 },
    { x: cabX + 0.5, y: cabY + 0.3, width: cabW - 1, height: 2.5, rx: 1, fill: "#d4802a" },
    { x: cabX + 1.2, y: cabY + 3, width: cabW - 2.4, height: 3.5, rx: 0.8, fill: "#5ba8d4", stroke: "#4890b8", strokeWidth: 0.3 },
    { x: cabX + 0.8, y: cabY + cabH - 2.5, width: cabW - 1.6, height: 2, rx: 0.5, fill: "#d4802a" },
    { x: cabX - 1.2, y: cabY + 3, width: 1, height: 2, rx: 0.3, fill: "#aaa" },
    { x: cabX + cabW + 0.2, y: cabY + 3, width: 1, height: 2, rx: 0.3, fill: "#aaa" },
  ];
}

function TruckOutline({
  x,
  y,
  w,
  h,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
}) {
  const cabW = w * 0.75;
  const cabH = 10;
  const gapH = 1;
  const cabX = x + (w - cabW) / 2;
  const cabY = y - gapH - cabH;

  return (
    <g>
      {truckCabParts(cabX, cabY, cabW, cabH).map((part, partIndex) => (
        <rect key={partIndex} {...part} />
      ))}
      <rect
        x={x + w / 2 - 1.5}
        y={y - gapH}
        width={3}
        height={gapH + 0.5}
        fill="#888"
      />
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={0.5}
        fill="#fff"
        stroke="#222"
        strokeWidth={0.6}
      />
    </g>
  );
}

const TRUCK_W = 17;
const TRUCK_H = 79;
const CAB_H = 10;
const GAP_H = 1;
const TRUCK_OFFSET_X = 5;
const TRUCK_OFFSET_Y = CAB_H + GAP_H + 2;
const VB_W = TRUCK_OFFSET_X + TRUCK_W + 5;
const VB_H = TRUCK_OFFSET_Y + TRUCK_H + 3;
const INNER_H = TRUCK_H - 2;

function DiagramFrame({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      className="w-full h-full"
      shapeRendering="crispEdges"
    >
      <TruckOutline x={TRUCK_OFFSET_X} y={TRUCK_OFFSET_Y} w={TRUCK_W} h={TRUCK_H} />
      {children}
    </svg>
  );
}

function GridDiagram({ rows, pw }: { rows: number; pw: number }) {
  const cols = 2;
  const gap = 1;
  const palletHeight = (INNER_H - (rows - 1) * gap) / rows;
  const innerW = cols * pw + (cols - 1) * gap;
  const offsetX = (TRUCK_W - innerW) / 2;
  return (
    <DiagramFrame>
      {Array.from({ length: rows }).map((_, rowIndex) =>
        Array.from({ length: cols }).map((_, colIndex) => (
          <PalletRect
            key={`${rowIndex}-${colIndex}`}
            x={TRUCK_OFFSET_X + offsetX + colIndex * (pw + gap)}
            y={TRUCK_OFFSET_Y + 1 + rowIndex * (palletHeight + gap)}
            w={pw}
            h={palletHeight}
          />
        )),
      )}
    </DiagramFrame>
  );
}

function StraightDiagram() {
  return <GridDiagram rows={10} pw={5} />;
}

function TurnedDiagram() {
  return <GridDiagram rows={13} pw={6} />;
}

function buildPinwheelRects(
  rowConfigs: Array<{ pw: number; ph: number }>,
  cols: number,
  gap: number,
) {
  const rects: Array<React.ReactElement> = [];
  let currentY = TRUCK_OFFSET_Y + 1;
  rowConfigs.forEach((row, rowIndex) => {
    const rowWidth = cols * row.pw + (cols - 1) * gap;
    const offsetX = TRUCK_OFFSET_X + (TRUCK_W - rowWidth) / 2;
    for (let colIndex = 0; colIndex < cols; colIndex++) {
      rects.push(
        <PalletRect
          key={`${rowIndex}-${colIndex}`}
          x={offsetX + colIndex * (row.pw + gap)}
          y={currentY}
          w={row.pw}
          h={row.ph}
        />,
      );
    }
    currentY += row.ph + gap;
  });
  return rects;
}

function PinwheelDiagram() {
  const gap = 1;
  const cols = 2;
  const numRows = 8;
  const scale = (INNER_H - (numRows - 1) * gap) / (numRows * 5.5);
  const straightPh = 6 * scale;
  const turnedPh = 5 * scale;
  const straightPw = 5;
  const turnedPw = 6;
  const rowConfigs = [
    { pw: straightPw, ph: straightPh },
    { pw: turnedPw, ph: turnedPh },
    { pw: straightPw, ph: straightPh },
    { pw: turnedPw, ph: turnedPh },
    { pw: straightPw, ph: straightPh },
    { pw: turnedPw, ph: turnedPh },
    { pw: straightPw, ph: straightPh },
    { pw: turnedPw, ph: turnedPh },
  ];

  return <DiagramFrame>{buildPinwheelRects(rowConfigs, cols, gap)}</DiagramFrame>;
}

export const LOADING_PATTERN_DIAGRAMS: Record<LoadingType, () => React.ReactElement> = {
  straight: StraightDiagram,
  turned: TurnedDiagram,
  pinwheel: PinwheelDiagram,
};
